import { OperatorElearningTopicCategoriesService } from '../../../../../api/api-services/operator-elearning-topic-categories.service';
import { OperatorElearningTopicTranslationService } from '../../../../../api/api-services/operator-elearning-topic-translation.service';
import { IOperatorElearningTopicTranslationUm } from '../../../../../api/operator-elearning-topic-translation-um.model';
import { IOperatorElearningTopicTranslationIm } from '../../../../../api/operator-elearning-topic-translation-im.model';
import { IOperatorElearningTopicCategoryVm } from '../../../../../api/operator-elearning-topic-category-vm.model';
import { OperatorElearningTopicService } from '../../../../../api/api-services/operator-elearning-topic.service';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  OnChanges,
} from '@angular/core';
import { IOperatorElearningTopicVm } from '../../../../../api/operator-elearning-topic-vm.model';
import { IOperatorElearningTopicUm } from '../../../../../api/operator-elearning-topic-um.model';
import { IDetailsControl } from '../../../../shared/models/details-control';
import { environment } from '../../../../../environments/environment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

interface UploadFileResultVm {
  publicVisible: boolean;
  success: boolean;
  size: number;
  fileUrl: string;
}

@Component({
  selector: 'app-topic-details',
  templateUrl: './topic-details.component.html',
  styleUrl: './topic-details.component.scss',
})
export class TopicDetailsComponent implements OnInit, OnChanges {
  @Input() topic!: IOperatorElearningTopicVm;
  @Input() topicList!: IOperatorElearningTopicVm[];
  @Input() topicIndex!: number;
  @Input() detailsControl: IDetailsControl = {
    isEditing: false,
    isOpen: false,
  };

  @Output() detailsControlChange = new EventEmitter<IDetailsControl>();
  @Output() refreshTopics = new EventEmitter<void>();

  private _operatorElearningTopicTranslationService = inject(
    OperatorElearningTopicTranslationService
  );
  private _operatorElearningTopicCategoriesService = inject(
    OperatorElearningTopicCategoriesService
  );
  private _operatorElearningTopicService = inject(
    OperatorElearningTopicService
  );
  private _messageService = inject(MessageService);
  private _http = inject(HttpClient);
  private _fb = inject(FormBuilder);

  public environment = environment;
  public isLoading = false;
  public isSaving = false;

  private skipFormReinitOnEditToggle = false;

  public editingThumbnail = false;
  public savingThumbnail = false;
  public editingCategory = false;
  public editingMainName = false;
  public savingCategory = false;
  public savingMainName = false;
  public editingOrder = false;
  public savingOrder = false;

  public availableCategories: IOperatorElearningTopicCategoryVm[] = [];

  public editingTranslationThumbnail: { [key: string]: boolean } = {};
  public savingTranslationThumbnail: { [key: string]: boolean } = {};
  public editingTranslationName: { [key: string]: boolean } = {};
  public savingTranslationName: { [key: string]: boolean } = {};

  public selectedTranslationFiles: { [languageCode: string]: File | null } = {};
  public selectedThumbnailFile: File | null = null;
  public mainNameForm!: FormGroup;

  public availableLanguages = [
    { code: 'en', name: 'Angielski', isDefault: false },
    { code: 'uk', name: 'Ukraiński', isDefault: false },
    { code: 'de', name: 'Niemiecki', isDefault: false },
  ];

  public translationNameForms: { [key: string]: FormGroup } = {};
  public translationForms: { [key: string]: FormGroup } = {};
  public editingTranslation: { [key: string]: boolean } = {};
  public savingTranslation: { [key: string]: boolean } = {};
  public topicForm!: FormGroup;

  ngOnInit(): void {
    this.loadAvailableCategories();
    this.initializeForms();
  }

  ngOnChanges(): void {
    if (this.topic && this.detailsControl.isOpen) {
      this.detailsControl.isEditing = false;
      this.detailsControlChange.emit(this.detailsControl);
      this.initializeForms();
    }
  }

  private loadAvailableCategories(): void {
    this._operatorElearningTopicCategoriesService.get().subscribe({
      next: (categories) => {
        this.availableCategories = categories;
      },
      error: (error) => {
        this.availableCategories = [];
      },
    });
  }

  private initializeForms(): void {
    if (!this.topic) return;

    this.topicForm = this._fb.group({
      id: [this.topic.id],
      name: [this.topic.name || '', Validators.required],
      order: [this.topic.order || 0, [Validators.min(0)]],
      elearningTopicCategoryId: [this.topic.elearningTopicCategoryId],
    });

    this.mainNameForm = this._fb.group({
      name: [this.topic.name || '', Validators.required],
    });

    this.translationForms = {};
    this.translationNameForms = {};
    this.editingTranslation = {};
    this.savingTranslation = {};
    this.editingTranslationName = {};
    this.savingTranslationName = {};
    this.editingTranslationThumbnail = {};
    this.savingTranslationThumbnail = {};

    this.availableLanguages.forEach((lang) => {
      if (lang.isDefault) {
        return;
      }

      const existingTranslation = this.topic.elearningTopicTranslation?.find(
        (t: any) => t.languageCode === lang.code
      );

      this.translationForms[lang.code] = this._fb.group({
        id: [existingTranslation?.id || 0],
        languageCode: [lang.code, Validators.required],
        elearningTopicId: [this.topic.id, Validators.required],
        name: [existingTranslation?.nameOverride || '', Validators.required],
        thumbnailUrl: [existingTranslation?.thumbnailUrlOverride || ''],
      });

      this.translationNameForms[lang.code] = this._fb.group({
        name: [existingTranslation?.nameOverride || '', Validators.required],
      });

      this.editingTranslation[lang.code] = false;
      this.savingTranslation[lang.code] = false;
      this.editingTranslationName[lang.code] = false;
      this.savingTranslationName[lang.code] = false;
      this.editingTranslationThumbnail[lang.code] = false;
      this.savingTranslationThumbnail[lang.code] = false;
    });
  }

  public closeDetails(): void {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
    this.detailsControlChange.emit(this.detailsControl);
    this.resetEditingStates();
  }

  public editToggle(): void {
    if (!this.detailsControl.isEditing && this.isAnyTranslationBeingEdited()) {
      return;
    }

    this.detailsControl.isEditing = !this.detailsControl.isEditing;
    this.detailsControlChange.emit(this.detailsControl);

    if (!this.detailsControl.isEditing) {
      if (this.skipFormReinitOnEditToggle) {
        this.skipFormReinitOnEditToggle = false;
        return;
      }
      this.initializeForms();
    }
  }

  public saveTopic(): void {
    if (this.topicForm.invalid) {
      this.markFormGroupTouched(this.topicForm);
      return;
    }

    this.isSaving = true;
    const formValue = this.topicForm.value;

    const originalTopic = { ...this.topic };

    const updateData: IOperatorElearningTopicUm = {
      id: this.topic.id,
      name: formValue.name,
      order: formValue.order,
      thumbnailUrl: this.topic.thumbnailUrl,
      elearningTopicCategoryId: formValue.elearningTopicCategoryId,
    };

    this.topic = {
      ...this.topic,
      name: formValue.name,
      order: formValue.order,
      elearningTopicCategoryId: formValue.elearningTopicCategoryId,
      elearningTopicCategoryName: this.getCategoryNameById(
        formValue.elearningTopicCategoryId
      ),
    };

    this._operatorElearningTopicService.put(updateData).subscribe({
      next: (response: any) => {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Dane zostały zaktualizowane',
        });

        this.skipFormReinitOnEditToggle = true;
        this.detailsControl.isEditing = false;
        this.detailsControlChange.emit(this.detailsControl);
        this.refreshTopics.emit();
        this.isSaving = false;
      },
      error: (error: any) => {
        this.topic = originalTopic;
        if (this.topicList && this.topicIndex >= 0) {
          this.topicList[this.topicIndex] = originalTopic;
        }

        this.initializeForms();

        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się zaktualizować danych',
        });
        this.isSaving = false;
      },
    });
  }

  public enableCategoryEditing(): void {
    this.editingCategory = true;
  }

  public cancelCategoryEditing(): void {
    this.editingCategory = false;
    this.topicForm.patchValue({
      elearningTopicCategoryId: this.topic.elearningTopicCategoryId,
    });
  }

  public enableOrderEditing(): void {
    this.editingOrder = true;
  }

  public cancelOrderEditing(): void {
    this.editingOrder = false;
    this.topicForm.patchValue({
      order: this.topic.order || 0,
    });
  }

  public enableMainNameEditing(): void {
    this.editingMainName = true;
  }

  public cancelMainNameEditing(): void {
    this.editingMainName = false;
    this.mainNameForm.patchValue({
      name: this.topic.name || '',
    });
  }

  public enableThumbnailEditing(): void {
    if (this.detailsControl.isEditing || this.isAnyTranslationBeingEdited()) {
      return;
    }
    this.editingThumbnail = true;
  }

  public cancelThumbnailEditing(): void {
    this.editingThumbnail = false;
    this.selectedThumbnailFile = null;
  }

  public saveMainName(): void {
    if (this.mainNameForm.invalid) {
      this.markFormGroupTouched(this.mainNameForm);
      return;
    }

    this.savingMainName = true;
    const formValue = this.mainNameForm.value;

    const updateData: IOperatorElearningTopicUm = {
      id: this.topic.id,
      name: formValue.name,
      order: this.topic.order,
      thumbnailUrl: this.topic.thumbnailUrl,
      elearningTopicCategoryId: this.topic.elearningTopicCategoryId,
    };

    this._operatorElearningTopicService.put(updateData).subscribe({
      next: (response: any) => {
        this.topic.name = formValue.name;

        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Nazwa główna została zaktualizowana',
        });

        this.editingMainName = false;
        this.refreshTopics.emit();
        this.savingMainName = false;
      },
      error: (error: any) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się zaktualizować nazwy głównej',
        });
        this.savingMainName = false;
      },
    });
  }

  public async saveThumbnail(): Promise<void> {
    if (!this.selectedThumbnailFile) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać plik zdjęcia',
      });
      return;
    }

    this.savingThumbnail = true;

    try {
      const uploadedUrl = await this.uploadImageToServer(
        this.selectedThumbnailFile
      );

      const updateData: IOperatorElearningTopicUm = {
        id: this.topic.id,
        name: this.topic.name,
        order: this.topic.order,
        thumbnailUrl: uploadedUrl,
        elearningTopicCategoryId: this.topic.elearningTopicCategoryId,
      };

      this._operatorElearningTopicService.put(updateData).subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Thumbnail został zaktualizowany',
          });

          this.editingThumbnail = false;
          this.selectedThumbnailFile = null;
          this.refreshTopics.emit();
          this.savingThumbnail = false;
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się zaktualizować thumbnail',
          });
          this.savingThumbnail = false;
        },
      });
    } catch (error) {
      this.savingThumbnail = false;
    }
  }

  public removeThumbnail(): void {
    this.savingThumbnail = true;

    const originalTopic = { ...this.topic };

    const updateData: IOperatorElearningTopicUm = {
      id: this.topic.id,
      name: this.topic.name,
      order: this.topic.order,
      thumbnailUrl: '',
      elearningTopicCategoryId: this.topic.elearningTopicCategoryId,
    };

    this.topic.thumbnailUrl = '';

    this._operatorElearningTopicService.put(updateData).subscribe({
      next: (response: any) => {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Thumbnail został usunięty',
        });

        this.refreshTopics.emit();
        this.savingThumbnail = false;
      },
      error: (error: any) => {
        this.topic = originalTopic;
        if (this.topicList && this.topicIndex >= 0) {
          this.topicList[this.topicIndex] = originalTopic;
        }

        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się usunąć thumbnail',
        });
        this.savingThumbnail = false;
      },
    });
  }

  public saveCategory(): void {
    if (this.topicForm.invalid) {
      return;
    }

    this.savingCategory = true;
    const formValue = this.topicForm.value;

    const updateData: IOperatorElearningTopicUm = {
      id: this.topic.id,
      name: this.topic.name,
      order: this.topic.order,
      thumbnailUrl: this.topic.thumbnailUrl,
      elearningTopicCategoryId: formValue.elearningTopicCategoryId,
    };

    this._operatorElearningTopicService.put(updateData).subscribe({
      next: (response: any) => {
        this.topic.elearningTopicCategoryId =
          formValue.elearningTopicCategoryId;
        this.topic.elearningTopicCategoryName = this.getCategoryNameById(
          formValue.elearningTopicCategoryId
        );

        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Dział został zaktualizowany',
        });

        this.editingCategory = false;
        this.refreshTopics.emit();
        this.savingCategory = false;
      },
      error: (error: any) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się zaktualizować działu',
        });
        this.savingCategory = false;
      },
    });
  }

  public saveOrder(): void {
    if (this.topicForm.invalid) {
      return;
    }

    this.savingOrder = true;
    const formValue = this.topicForm.value;

    const updateData: IOperatorElearningTopicUm = {
      id: this.topic.id,
      name: this.topic.name,
      order: formValue.order,
      thumbnailUrl: this.topic.thumbnailUrl,
      elearningTopicCategoryId: this.topic.elearningTopicCategoryId,
    };

    this._operatorElearningTopicService.put(updateData).subscribe({
      next: (response: any) => {
        this.topic.order = formValue.order;

        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Kolejność wyświetlania została zaktualizowana',
        });

        this.editingOrder = false;
        this.refreshTopics.emit();
        this.savingOrder = false;
      },
      error: (error: any) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się zaktualizować kolejności wyświetlania',
        });
        this.savingOrder = false;
      },
    });
  }

  public hideDialog(): void {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
    this.resetEditingStates();
    this.detailsControlChange.emit(this.detailsControl);
  }

  public enableTranslationEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      return;
    }
    this.editingTranslation[languageCode] = true;
  }

  public cancelTranslationEditing(languageCode: string): void {
    this.editingTranslation[languageCode] = false;
    const existingTranslation = this.topic.elearningTopicTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );

    if (existingTranslation) {
      this.translationForms[languageCode].patchValue({
        name: existingTranslation.nameOverride,
        thumbnailUrl: existingTranslation.thumbnailUrlOverride,
      });
    } else {
      this.translationForms[languageCode].patchValue({
        name: '',
        thumbnailUrl: '',
      });
    }

    this.selectedTranslationFiles[languageCode] = null;
  }

  public async saveTranslation(languageCode: string): Promise<void> {
    const form = this.translationForms[languageCode];
    if (form.invalid) {
      this.markFormGroupTouched(form);
      return;
    }

    this.savingTranslation[languageCode] = true;
    const formValue = form.value;
    const existingTranslation = this.topic.elearningTopicTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );

    try {
      let thumbnailUrl = formValue.thumbnailUrl;

      if (this.selectedTranslationFiles[languageCode]) {
        thumbnailUrl = await this.uploadImageToServer(
          this.selectedTranslationFiles[languageCode]!
        );
      }

      if (
        existingTranslation &&
        existingTranslation.id &&
        existingTranslation.id > 0
      ) {
        const updateData: IOperatorElearningTopicTranslationUm = {
          id: existingTranslation.id,
          name: formValue.name,
          thumbnailUrl: thumbnailUrl,
        };

        this._operatorElearningTopicTranslationService
          .put(updateData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationSaveSuccess(
                languageCode,
                {
                  ...existingTranslation,
                  nameOverride: formValue.name,
                  thumbnailUrlOverride: thumbnailUrl,
                },
                'zaktualizowane'
              );
              this.selectedTranslationFiles[languageCode] = null;
            },
            error: (error: any) => {
              this.handleTranslationSaveError(
                languageCode,
                error,
                'zaktualizować'
              );
            },
          });
      } else {
        const newTranslation: IOperatorElearningTopicTranslationIm = {
          elearningTopicId: formValue.elearningTopicId,
          name: formValue.name,
          languageCode: formValue.languageCode,
          thumbnailUrl: thumbnailUrl,
        };

        this._operatorElearningTopicTranslationService
          .post(newTranslation)
          .subscribe({
            next: (response: any) => {
              form.patchValue({ id: response.id });
              this.handleTranslationSaveSuccess(
                languageCode,
                response,
                'utworzone'
              );
              this.selectedTranslationFiles[languageCode] = null;
            },
            error: (error: any) => {
              this.handleTranslationSaveError(languageCode, error, 'utworzyć');
            },
          });
      }
    } catch (error) {
      this.savingTranslation[languageCode] = false;
    }
  }

  public deleteTranslation(languageCode: string): void {
    const existingTranslation = this.topic.elearningTopicTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );

    if (!existingTranslation || existingTranslation.id <= 0) {
      this.translationForms[languageCode].patchValue({
        name: '',
        thumbnailUrl: '',
      });
      return;
    }

    this.savingTranslation[languageCode] = true;

    this._operatorElearningTopicTranslationService
      .delete(existingTranslation.id)
      .subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: `Tłumaczenie zostało usunięte`,
          });

          if (this.topic.elearningTopicTranslation) {
            this.topic.elearningTopicTranslation =
              this.topic.elearningTopicTranslation.filter(
                (t: any) => t.languageCode !== languageCode
              );
          }

          this.translationForms[languageCode].patchValue({
            id: 0,
            name: '',
            thumbnailUrl: '',
          });

          this.savingTranslation[languageCode] = false;
          this.editingTranslation[languageCode] = false;
          this.refreshTopics.emit();
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się usunąć tłumaczenia',
          });
          this.savingTranslation[languageCode] = false;
        },
      });
  }

  public hasTranslation(languageCode: string): boolean {
    return (
      this.topic.elearningTopicTranslation?.some(
        (t: any) => t.languageCode === languageCode && t.nameOverride
      ) || false
    );
  }

  public getTranslationName(languageCode: string): string {
    const translation = this.topic.elearningTopicTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );
    return translation?.nameOverride || '';
  }

  public getTranslationThumbnail(languageCode: string): string {
    const translation = this.topic.elearningTopicTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );
    return translation?.thumbnailUrlOverride || '';
  }

  public onMainThumbnailSelected(files: File | File[]): void {
    if (files instanceof File) {
      this.selectedThumbnailFile = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name}`,
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedThumbnailFile = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files[0].name}`,
      });
    }
  }

  public onTranslationThumbnailSelected(
    files: File | File[],
    languageCode: string
  ): void {
    if (files instanceof File) {
      this.selectedTranslationFiles[languageCode] = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name} dla języka ${this.getLanguageName(
          languageCode
        )}`,
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedTranslationFiles[languageCode] = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${
          files[0].name
        } dla języka ${this.getLanguageName(languageCode)}`,
      });
    }
  }

  public getLanguageName(languageCode: string): string {
    const language = this.availableLanguages.find(
      (lang) => lang.code === languageCode
    );
    return language?.name || languageCode;
  }

  private async uploadImageToServer(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('publicVisible', 'true');

      const response = await firstValueFrom(
        this._http.post<UploadFileResultVm>(
          `${environment.webApiUrl}/UploadMedia`,
          formData
        )
      );

      if (response.success) {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: `Zdjęcie zostało przesłane.`,
        });
        return response.fileUrl;
      } else {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się przesłać zdjęcia.',
        });
        throw new Error('Upload failed');
      }
    } catch (error) {
      this._messageService.add({
        severity: 'error',
        summary: 'Błąd',
        detail: 'Wystąpił błąd podczas przesyłania zdjęcia.',
      });
      throw error;
    }
  }

  private handleTranslationSaveSuccess(
    languageCode: string,
    translationData: any,
    action: string
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: `Tłumaczenie zostało ${action}`,
    });

    if (!this.topic.elearningTopicTranslation) {
      this.topic.elearningTopicTranslation = [];
    }

    const existingIndex = this.topic.elearningTopicTranslation.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.topic.elearningTopicTranslation[existingIndex] = translationData;
    } else {
      this.topic.elearningTopicTranslation.push(translationData);
    }

    this.editingTranslation[languageCode] = false;
    this.savingTranslation[languageCode] = false;
    this.refreshTopics.emit();
  }

  private handleTranslationSaveError(
    languageCode: string,
    error: any,
    action: string
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: `Nie udało się ${action} tłumaczenia`,
    });
    this.savingTranslation[languageCode] = false;
  }

  private resetEditingStates(): void {
    Object.keys(this.editingTranslation).forEach((lang) => {
      // Skip default language (Polish)
      const language = this.availableLanguages.find((l) => l.code === lang);
      if (language?.isDefault) {
        return;
      }

      this.editingTranslation[lang] = false;
      this.savingTranslation[lang] = false;
      this.editingTranslationName[lang] = false;
      this.savingTranslationName[lang] = false;
      this.editingTranslationThumbnail[lang] = false;
      this.savingTranslationThumbnail[lang] = false;
    });
    this.editingCategory = false;
    this.editingMainName = false;
    this.editingThumbnail = false;
    this.savingCategory = false;
    this.savingMainName = false;
    this.savingThumbnail = false;
  }

  public enableTranslationNameEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      return;
    }
    this.editingTranslationName[languageCode] = true;
  }

  public cancelTranslationNameEditing(languageCode: string): void {
    this.editingTranslationName[languageCode] = false;
    const existingTranslation = this.topic.elearningTopicTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );

    this.translationNameForms[languageCode].patchValue({
      name: existingTranslation?.nameOverride || '',
    });
  }

  public saveTranslationName(languageCode: string): void {
    const form = this.translationNameForms[languageCode];
    if (form.invalid) {
      this.markFormGroupTouched(form);
      return;
    }

    this.savingTranslationName[languageCode] = true;
    const formValue = form.value;
    const existingTranslation = this.topic.elearningTopicTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );

    if (
      existingTranslation &&
      existingTranslation.id &&
      existingTranslation.id > 0
    ) {
      const updateData: IOperatorElearningTopicTranslationUm = {
        id: existingTranslation.id,
        name: formValue.name,
        thumbnailUrl: existingTranslation.thumbnailUrlOverride || '',
      };

      this._operatorElearningTopicTranslationService.put(updateData).subscribe({
        next: (response: any) => {
          this.handleTranslationNameSaveSuccess(languageCode, formValue.name);
        },
        error: (error: any) => {
          this.handleTranslationNameSaveError(languageCode, error);
        },
      });
    } else {
      const createData: IOperatorElearningTopicTranslationIm = {
        name: formValue.name,
        languageCode: languageCode,
        elearningTopicId: this.topic.id,
        thumbnailUrl: '',
      };

      this._operatorElearningTopicTranslationService
        .post(createData)
        .subscribe({
          next: (response: any) => {
            this.handleTranslationNameSaveSuccess(
              languageCode,
              formValue.name,
              response
            );
          },
          error: (error: any) => {
            this.handleTranslationNameSaveError(languageCode, error);
          },
        });
    }
  }

  public enableTranslationThumbnailEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail:
          'Zakończ edycję głównego formularza lub innego tłumaczenia przed rozpoczęciem edycji tego tłumaczenia',
      });
      return;
    }
    this.editingTranslationThumbnail[languageCode] = true;
  }

  public cancelTranslationThumbnailEditing(languageCode: string): void {
    this.editingTranslationThumbnail[languageCode] = false;
    this.selectedTranslationFiles[languageCode] = null;
  }

  public async saveTranslationThumbnail(languageCode: string): Promise<void> {
    if (!this.selectedTranslationFiles[languageCode]) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać plik zdjęcia',
      });
      return;
    }

    this.savingTranslationThumbnail[languageCode] = true;
    const existingTranslation = this.topic.elearningTopicTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );

    try {
      const uploadedUrl = await this.uploadImageToServer(
        this.selectedTranslationFiles[languageCode]!
      );

      if (
        existingTranslation &&
        existingTranslation.id &&
        existingTranslation.id > 0
      ) {
        const updateData: IOperatorElearningTopicTranslationUm = {
          id: existingTranslation.id,
          name: existingTranslation.nameOverride || '',
          thumbnailUrl: uploadedUrl,
        };

        this._operatorElearningTopicTranslationService
          .put(updateData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationThumbnailSaveSuccess(
                languageCode,
                uploadedUrl
              );
            },
            error: (error: any) => {
              this.handleTranslationThumbnailSaveError(languageCode, error);
            },
          });
      } else {
        const createData: IOperatorElearningTopicTranslationIm = {
          name: '',
          languageCode: languageCode,
          elearningTopicId: this.topic.id,
          thumbnailUrl: uploadedUrl,
        };

        this._operatorElearningTopicTranslationService
          .post(createData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationThumbnailSaveSuccess(
                languageCode,
                uploadedUrl,
                response
              );
            },
            error: (error: any) => {
              this.handleTranslationThumbnailSaveError(languageCode, error);
            },
          });
      }
    } catch (error) {
      this.savingTranslationThumbnail[languageCode] = false;
    }
  }

  private handleTranslationNameSaveSuccess(
    languageCode: string,
    name: string,
    response?: any
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Nazwa tłumaczenia została zaktualizowana',
    });

    if (!this.topic.elearningTopicTranslation) {
      this.topic.elearningTopicTranslation = [];
    }

    const existingIndex = this.topic.elearningTopicTranslation.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.topic.elearningTopicTranslation[existingIndex].nameOverride = name;
    } else {
      const language = this.availableLanguages.find(
        (l) => l.code === languageCode
      );
      this.topic.elearningTopicTranslation.push({
        id: response?.id || 0,
        nameOverride: name,
        thumbnailUrlOverride: '',
        languageCode: languageCode,
      });
    }

    this.editingTranslationName[languageCode] = false;
    this.savingTranslationName[languageCode] = false;
    this.refreshTopics.emit();
  }

  private handleTranslationNameSaveError(
    languageCode: string,
    error: any
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się zaktualizować nazwy tłumaczenia',
    });
    this.savingTranslationName[languageCode] = false;
  }

  private handleTranslationThumbnailSaveSuccess(
    languageCode: string,
    thumbnailUrl: string,
    response?: any
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Thumbnail tłumaczenia został zaktualizowany',
    });

    if (!this.topic.elearningTopicTranslation) {
      this.topic.elearningTopicTranslation = [];
    }

    const existingIndex = this.topic.elearningTopicTranslation.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.topic.elearningTopicTranslation[existingIndex].thumbnailUrlOverride =
        thumbnailUrl;
    } else {
      const language = this.availableLanguages.find(
        (l) => l.code === languageCode
      );
      this.topic.elearningTopicTranslation.push({
        id: response?.id || 0,
        nameOverride: '',
        thumbnailUrlOverride: thumbnailUrl,
        languageCode: languageCode,
      });
    }

    this.editingTranslationThumbnail[languageCode] = false;
    this.savingTranslationThumbnail[languageCode] = false;
    this.selectedTranslationFiles[languageCode] = null;
    this.refreshTopics.emit();
  }

  private handleTranslationThumbnailSaveError(
    languageCode: string,
    error: any
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się zaktualizować thumbnail tłumaczenia',
    });
    this.savingTranslationThumbnail[languageCode] = false;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control) {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  private getCategoryNameById(categoryId: number): string {
    const category = this.availableCategories.find(
      (cat) => cat.id === categoryId
    );
    return category ? category.name : '';
  }

  public isAnyTranslationBeingEdited(): boolean {
    return (
      Object.values(this.editingTranslation).some((isEditing) => isEditing) ||
      Object.values(this.editingTranslationName).some(
        (isEditing) => isEditing
      ) ||
      Object.values(this.editingTranslationThumbnail).some(
        (isEditing) => isEditing
      ) ||
      this.editingThumbnail
    );
  }

  public canEditTranslation(languageCode: string): boolean {
    return (
      !this.detailsControl.isEditing &&
      !this.isAnyOtherTranslationBeingEdited(languageCode)
    );
  }

  public canEditTranslationField(
    languageCode: string,
    fieldType: string
  ): boolean {
    return (
      !this.detailsControl.isEditing &&
      this.canEditTranslation(languageCode) &&
      !this.isAnyOtherFieldBeingEditedInSameLanguage(languageCode, fieldType)
    );
  }

  private isAnyOtherTranslationBeingEdited(
    excludeLanguageCode: string
  ): boolean {
    return Object.keys(this.editingTranslation)
      .filter((lang) => lang !== excludeLanguageCode)
      .some(
        (lang) =>
          this.editingTranslation[lang] ||
          this.editingTranslationName[lang] ||
          this.editingTranslationThumbnail[lang]
      );
  }

  private isAnyOtherFieldBeingEditedInSameLanguage(
    languageCode: string,
    currentFieldType: string
  ): boolean {
    const fieldEditingStates = {
      name: this.editingTranslationName[languageCode] || false,
      thumbnail: this.editingTranslationThumbnail[languageCode] || false,
      general: this.editingTranslation[languageCode] || false,
    };

    return Object.keys(fieldEditingStates)
      .filter((field) => field !== currentFieldType)
      .some(
        (field) => fieldEditingStates[field as keyof typeof fieldEditingStates]
      );
  }

  deleteTopic() {
    this._operatorElearningTopicService.delete(this.topic.id).subscribe({
      next: (res) => {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Rozdział został usunięty',
        });
        this.hideDialog();
        this.refreshTopics.emit();
      },
      error: (error) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail:
            'Nie udało się usunąć rozdziału, upewnij się, że nie ma powiązanych slajdów',
        });
      },
    });
  }
}
